/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useDispatch, useSelector } from 'react-redux';
import { DraggablePlugin } from './DraggablePlugin';
import { usePluginCatalog } from '../../api/hooks';
import { getOverlayPlugins, pluginsActions } from '../../store';
import type { StoreWithPlugins } from '../../store';
import { PluginComponentConnect } from '../PluginComponentConnect';
import { getPluginKey, usePlugin } from '../PluginContext';

interface OverlayPluginWindowsProps {
  panelId: string;
}

export const OverlayPluginWindows = ({
  panelId,
}: OverlayPluginWindowsProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { data: pluginCatalog } = usePluginCatalog();
  const { loadedPlugins } = usePlugin();
  const overlayPlugins = useSelector((state: StoreWithPlugins) =>
    getOverlayPlugins(state, panelId),
  );

  const handleClose = (pluginId: string): void => {
    const pluginKey = getPluginKey(
      pluginId,
      pluginCatalog?.[pluginId]?.version,
    );
    const plugin = loadedPlugins[pluginKey] ?? loadedPlugins[pluginId];
    const closeBehavior = plugin?.closeBehavior ?? 'disable';
    const closeAction =
      closeBehavior === 'hide'
        ? pluginsActions.hideOverlayPlugin
        : pluginsActions.removeOverlayPlugin;
    dispatch(closeAction({ panelId, pluginId }));
  };

  return (
    <>
      {overlayPlugins.map(({ pluginId, isVisible }) => (
        <DraggablePlugin
          isVisible={isVisible}
          key={pluginId}
          onClose={(): void => handleClose(pluginId)}
          title={pluginCatalog?.[pluginId]?.name ?? pluginId}
        >
          <PluginComponentConnect
            hostPanelId={panelId}
            pluginId={pluginId}
            sourceId={`${panelId}-${pluginId}`}
          />
        </DraggablePlugin>
      ))}
    </>
  );
};
